// One renderer for every place help shows: the editor's preview, Publish, the
// website and the Scout. It turns an article's blocks into HTML; the page
// around it (header, navigation, stylesheet) belongs to each target. Every
// piece of text is escaped, and only our own markup is ever written.

import type { Rules } from './checks';
import { RULES } from './checks';
import { escapeHtml, renderInline } from './inline';
import { APP_NAMES, type ArticleContent, type ArticleRef, type HelpBlock, type MediaInfo } from './types';

export type Target = 'scout' | 'web';

export interface RenderContext {
  target: Target;
  media: Record<string, MediaInfo>;
  /** Other articles by topic ID, for links and Related. */
  articles: Record<string, ArticleRef>;
  rules?: Rules;
}

/** Attribute values: escaped, and a URL only when it is relative or https. */
const attr = (v: string | number) => escapeHtml(String(v));

export function safeUrl(url: string): string {
  const u = url.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(u) && !/^https:/i.test(u)) return '#';
  if (u.startsWith('//')) return '#';
  return u;
}

export function duration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (!m) return `${s} s`;
  return s ? `${m} min ${s} s` : `${m} min`;
}

export function slugify(text: string): string {
  return text.replace(/\*\*/g, '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
}

const missing = (what: string) => `<p class="hx-missing">Missing ${escapeHtml(what)}</p>`;

function img(m: MediaInfo): string {
  const size = m.width && m.height ? ` width="${attr(m.width)}" height="${attr(m.height)}"` : '';
  return `<img src="${attr(safeUrl(m.src))}" alt="${attr(m.alt)}"${size} loading="lazy" decoding="async">`;
}

function picture(m: MediaInfo): string {
  if (m.kind !== 'clip') return img(m);
  const still = m.stillSrc ? `<source media="(prefers-reduced-motion: reduce)" srcset="${attr(safeUrl(m.stillSrc))}">` : '';
  return `<picture>${still}${img(m)}</picture>`;
}

function figure(m: MediaInfo, caption?: string): string {
  const cap = caption ? `<figcaption>${renderInline(caption)}</figcaption>` : '';
  if (m.kind === 'clip') return `<figure class="hx-figure hx-clip" data-clip>${picture(m)}<span class="hx-badge">CLIP</span>${cap}</figure>`;
  return `<figure class="hx-figure">${img(m)}${cap}</figure>`;
}

function video(m: MediaInfo, b: Extract<HelpBlock, { type: 'video' }>, ctx: RenderContext): string {
  const len = m.durationS ? duration(m.durationS) : null;
  if (ctx.target === 'scout') {
    const data = [
      ` data-video-src="${attr(safeUrl(m.src))}"`,
      m.stillSrc ? ` data-poster="${attr(safeUrl(m.stillSrc))}"` : '',
      m.captionsSrc ? ` data-captions-src="${attr(safeUrl(m.captionsSrc))}"` : '',
    ].join('');
    const meta = `${len ? `${len}. ` : ''}Needs internet: connect to Wi-Fi to watch.`;
    return `<figure class="hx-video"${data}><div class="hx-video-row"><span class="hx-video-thumb" aria-hidden="true"></span>`
      + `<span class="hx-video-text"><span class="hx-video-title">Video: ${escapeHtml(m.alt)}</span><span class="hx-video-meta">${meta}</span></span>`
      + `<button type="button" class="hx-watch" disabled>WATCH</button></div></figure>`;
  }
  const poster = m.stillSrc ? ` poster="${attr(safeUrl(m.stillSrc))}"` : '';
  const track = m.captionsSrc ? `<track kind="captions" src="${attr(safeUrl(m.captionsSrc))}" srclang="en" label="English" default>` : '';
  const guide = b.stepsIn && ctx.articles[b.stepsIn] ? ` Prefer to read? <a href="${attr(safeUrl(ctx.articles[b.stepsIn].href))}">Read it as a guide</a>` : '';
  const parts = [`<span class="hx-video-title">${escapeHtml(m.alt)}</span>`, len, m.captionsSrc ? 'Captions.' : null].filter(Boolean).join(' · ');
  return `<figure class="hx-video"><video controls preload="none"${poster} src="${attr(safeUrl(m.src))}">${track}</video><figcaption>${parts}${guide}</figcaption></figure>`;
}

function block(b: HelpBlock, ctx: RenderContext, ids: Map<string, number>): string {
  switch (b.type) {
    case 'paragraph': return `<p>${renderInline(b.text)}</p>`;
    case 'subheading': {
      const base = slugify(b.text);
      const n = (ids.get(base) ?? 0) + 1;
      ids.set(base, n);
      return `<h2 id="${attr(n > 1 ? `${base}-${n}` : base)}">${renderInline(b.text)}</h2>`;
    }
    case 'note': return `<div class="hx-note" role="note"><p>${renderInline(b.text)}</p></div>`;
    case 'steps': return `<ol class="hx-steps">${b.items.map((s, i) => {
      const m = s.mediaId ? ctx.media[s.mediaId] : null;
      const media = s.mediaId ? (m ? figure(m) : missing('image')) : '';
      return `<li><span class="hx-n" aria-hidden="true">${i + 1}</span><div class="hx-step"><p>${renderInline(s.text)}</p>${media}</div></li>`;
    }).join('')}</ol>`;
    case 'image': case 'clip': {
      const m = ctx.media[b.mediaId];
      return m ? figure(m, b.caption) : missing(b.type);
    }
    case 'video': {
      const m = ctx.media[b.mediaId];
      return m ? video(m, b, ctx) : missing('video');
    }
    case 'table':
      return `<div class="hx-table"><table><thead><tr>${b.head.map((h) => `<th scope="col">${renderInline(h)}</th>`).join('')}</tr></thead>`
        + `<tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${renderInline(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
    case 'link': {
      const ref = ctx.articles[b.topicId];
      if (!ref) return `<p class="hx-link hx-missing">${escapeHtml(b.topicId)}</p>`;
      return `<p class="hx-link"><a href="${attr(safeUrl(ref.href))}">${b.text ? renderInline(b.text) : escapeHtml(ref.title)}</a></p>`;
    }
  }
}

export function renderBody(blocks: HelpBlock[], ctx: RenderContext): string {
  const ids = new Map<string, number>();
  return blocks.map((b) => block(b, ctx, ids)).join('');
}

/** The subheadings, for an On this page list: [{ id, text }]. */
export function outline(blocks: HelpBlock[]): { id: string; text: string }[] {
  const ids = new Map<string, number>();
  return blocks.flatMap((b) => {
    if (b.type !== 'subheading') return [];
    const base = slugify(b.text);
    const n = (ids.get(base) ?? 0) + 1;
    ids.set(base, n);
    return [{ id: n > 1 ? `${base}-${n}` : base, text: b.text.replace(/\*\*/g, '') }];
  });
}

export function renderArticle(a: ArticleContent, ctx: RenderContext): string {
  const rules = ctx.rules ?? RULES;
  const facts = [
    a.whereToFind ? ['Where', renderInline(a.whereToFind)] : null,
    a.requirements ? ['Needs', renderInline(a.requirements)] : null,
    a.needsSoftware.length ? ['Software', a.needsSoftware.map((s) => `${escapeHtml(APP_NAMES[s.app] ?? s.app)} ${escapeHtml(s.version)} or later`).join(', ')] : null,
    a.plans.length ? ['Plans', a.plans.map(escapeHtml).join(', ')] : null,
  ].filter((f): f is string[] => f !== null);
  const factList = facts.length ? `<dl class="hx-facts">${facts.map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`).join('')}</dl>` : '';

  let safety = '';
  if (a.safety) {
    const line = rules.safetyLines[a.safety];
    safety = line
      ? `<div class="hx-safety" role="note"><p><strong class="hx-safety-k">Safety:</strong> ${escapeHtml(line)}</p></div>`
      : `<div class="hx-safety hx-missing" role="note"><p>The ${a.safety} safety line has no approved wording yet.</p></div>`;
  }

  const related = a.related.map((t) => ctx.articles[t]).filter((r): r is ArticleRef => !!r);
  const relatedList = related.length
    ? `<nav class="hx-related" aria-label="Related"><h2>Related</h2><ul>${related.map((r) => `<li><a href="${attr(safeUrl(r.href))}">${escapeHtml(r.title)}</a></li>`).join('')}</ul></nav>`
    : '';

  return `<article class="hx-article" data-topic="${attr(a.topicId)}">`
    + `<header class="hx-head"><h1>${renderInline(a.title)}</h1><p class="hx-summary">${renderInline(a.summary)}</p></header>`
    + factList + safety
    + `<div class="hx-body">${renderBody(a.body, ctx)}</div>`
    + relatedList
    + '</article>';
}
